import {
  Avatar,
  ButtonBase,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { DatePickerInput } from 'Components/HookFormInputs/DatePickerInput';
import { TextInput } from 'Components/HookFormInputs/TextInput';
import { defaultMinDate } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleX } from 'Images';
import { useContext, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Buttons } from './Buttons';
import { SelectedInvoiceFile } from '../SelectedInvoiceFile';
import { Subtitle } from './Subtitle';
import { PriceInput } from 'Components/HookFormInputs/PriceInput';
import {
  AlternativeMedicineContext,
  IAlternativeMedicineInvoice,
} from 'Context/AlternativeMedicine';
import { useAlternativeMedicineForm } from 'Hooks/useAlternativeMedicineForm';
import { ControlledAutocomplete } from 'Components';
import { TreatmentTypeTitle } from '../../TreatmentTypeTitle';
import { WarningMessageWithIcon } from 'Components/WarningMessageWithIcon';
import { alternativeMedicineRequiredMessages } from 'Regex';

interface IForm {
  invoice: IAlternativeMedicineInvoice;
}

export const maxInvoiceLength = 15;

export const Form = ({ invoice }: IForm) => {
  const {
    handleAddInvoice,
    handleCloseModal,
    watchFile,
    checkIfInvoiceExists,
    editedInvoiceIndex,
    onReturnFromEdit,
    formData,
    setFormData,
    treatmentTypeOptions,
  } = useContext(AlternativeMedicineContext);
  const {
    AlternativeMedicineForm,
    AlternativeMedicineForm: {
      register,
      formState,
      watch,
      handleSubmit,
      reset,
      resetField,
    },
  } = useAlternativeMedicineForm(checkIfInvoiceExists, invoice);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();

  const onSubmit = async () => {
    if (editedInvoiceIndex !== null && !formData.treatmentType) {
      return;
    }
    await handleSubmit((data) => {
      handleAddInvoice({
        ...invoice,
        invoiceTotal: data.invoiceTotal,
        vendorName: data.vendorName,
        invoiceDate: data.invoiceDate,
        invoiceId: data.invoiceId,
        treatmentTypeName: formData.treatmentType || '',
      });
      reset();
      handleCloseModal();
    })();
  };

  const resetAndExit = () => {
    reset();
    handleCloseModal();
  };

  const getEndAdornment = (
    inputName: 'invoiceId' | 'invoiceTotal' | 'vendorName'
  ) =>
    !!watch(inputName) ? (
      <InputAdornment position='end'>
        <IconButton
          onClick={() => resetField(inputName, { defaultValue: '' })}
          sx={{ width: '20px', height: '20px' }}
        >
          <Avatar
            alt={Dictionary[DICTIONARY.General].reset}
            src={PurpleX}
            sx={{ width: '20px', height: '20px' }}
          />
        </IconButton>
      </InputAdornment>
    ) : undefined;

  const isNoDataError = useMemo(
    () =>
      invoice.invoiceId === '' &&
      invoice.invoiceDate === null &&
      invoice.invoiceTotal === null &&
      invoice.vendorName === null,
    [
      invoice.invoiceTotal,
      invoice.invoiceDate,
      invoice.invoiceId,
      invoice.vendorName,
    ]
  );

  const checkIfShowCancelEditModal = () =>
    editedInvoiceIndex !== null &&
    (invoice.invoiceId !== watch('invoiceId') ||
      invoice.invoiceDate?.toString() !== watch('invoiceDate')?.toString() ||
      invoice.invoiceTotal !== watch('invoiceTotal'));

  const getInvoiceNumberErrors = () =>
    !!(formState.errors as any)?.invoiceId?.message ||
    (!isNoDataError && invoice.invoiceId === '' && !watch('invoiceId'))
      ? {
          message: (
            <WarningMessageWithIcon
              message={
                (formState.errors as any)?.invoiceId?.message ||
                dictionary.form.missingDataError
              }
              id='invoiceId-error-text'
            />
          ),
        }
      : undefined;
  const getVendorNameErrors = () =>
    !!(formState.errors as any)?.vendorName?.message ||
    (!isNoDataError && invoice.vendorName === null && !watch('vendorName'))
      ? {
          message: (
            <WarningMessageWithIcon
              message={
                (formState.errors as any)?.vendorName?.message ||
                dictionary.form.missingDataError
              }
              id='vendorName-error-text'
            />
          ),
        }
      : undefined;

  const getTreatmentTypeErrors = () =>
    !isNoDataError && !formData.treatmentType
      ? {
          message: (
            <WarningMessageWithIcon
              message={alternativeMedicineRequiredMessages.treatmentType}
              id='treatment-type-error-text'
            />
          ),
        }
      : undefined;

  const getPriceInputErrors = () =>
    !!(formState.errors as any)?.invoiceTotal ||
    (!isNoDataError && invoice.invoiceTotal === null && !watch('invoiceTotal'))
      ? {
          message: (
            <WarningMessageWithIcon
              message={
                (formState.errors as any)?.invoiceTotal?.message ||
                dictionary.form.missingDataError
              }
              id='price-error-text'
            />
          ),
        }
      : undefined;

  const limitMaxLength = (e: any) =>
    (e.target.value = e.target.value.slice(0, maxInvoiceLength));

  return (
    <FormProvider {...AlternativeMedicineForm}>
      <Stack
        sx={{
          alignItems: 'center',
          p: isDesktop ? '80px 0px 48px 140px' : '100px 24px 48px',
          width: isDesktop ? '940px' : '100%',
          gap: isDesktop ? '40px' : '12px',
        }}
      >
        <TreatmentTypeTitle />
        <Typography component={'h1'} variant='h3'>
          {editedInvoiceIndex !== null
            ? dictionary.form.editTitle
            : dictionary.form.title}{' '}
          {` - ${dictionary.form.alternativeMedicineFormTitle}`}
        </Typography>
        <Stack sx={{ gap: isDesktop ? '48px' : '32px' }}>
          <Subtitle
            isEdit={editedInvoiceIndex !== null}
            isNoDataError={isNoDataError}
          />
          <Stack
            sx={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              width: '100%',
              gap: isDesktop ? '48px' : '20px',
              mt: isDesktop ? '-16px' : undefined,
              '& .MuiOutlinedInput-input': { textAlign: 'left' },
            }}
          >
            <TextInput
              label={dictionary.form.invoiceNumber}
              register={register('invoiceId', { onChange: limitMaxLength })}
              shrink={!!watch('invoiceId')}
              errors={getInvoiceNumberErrors()}
              sx={{
                maxWidth: '376px',
                mt: isDesktop ? '4px' : undefined,
                mb: '-6px',
              }}
              endAdornment={getEndAdornment('invoiceId')}
              aria-labelledby={
                !!(formState.errors as any)?.invoiceId?.message
                  ? 'invoiceId-error-text'
                  : undefined
              }
            />
            <TextInput
              label={dictionary.supplierName}
              register={register('vendorName', { onChange: limitMaxLength })}
              shrink={!!watch('vendorName')}
              errors={getVendorNameErrors()}
              sx={{
                maxWidth: '376px',
                mt: isDesktop ? '4px' : undefined,
                mb: '-6px',
              }}
              endAdornment={getEndAdornment('vendorName')}
            />
            <DatePickerInput
              label={dictionary.form.fromDate}
              registerName='invoiceDate'
              minDate={defaultMinDate}
              maxDate={new Date()}
              maxWidth={isDesktop ? '376px' : '100%'}
              minWidth={isDesktop ? '376px' : '100%'}
              errorMessage={
                !isNoDataError &&
                invoice.invoiceDate === null &&
                !watch('invoiceDate')
                  ? dictionary.form.missingDataError
                  : undefined
              }
            />
            {editedInvoiceIndex !== null && (
              <ControlledAutocomplete
                value={formData.treatmentType}
                setValue={(newValue: string | null) => {
                  setFormData({
                    ...formData,
                    treatmentType: newValue || '',
                  });
                }}
                options={treatmentTypeOptions.map(
                  (treatment) => treatment.value
                )}
                label={dictionary.treatmentType}
                inputValue={formData.treatmentType}
                style={{ width: isDesktop ? '376px' : '100%' }}
                endAdornment={
                  <Stack
                    direction={'row'}
                    marginRight={'5px'}
                    width={'auto'}
                    gap={'15px'}
                    position={'absolute'}
                    right={'8px'}
                  >
                    <ButtonBase
                      onClick={() =>
                        setFormData({
                          ...formData,
                          treatmentType: '',
                        })
                      }
                    >
                      <Avatar
                        alt={''}
                        src={PurpleX}
                        sx={{ width: '20px', height: '20px' }}
                      />
                    </ButtonBase>
                  </Stack>
                }
                error={!!getTreatmentTypeErrors()}
                helperText={getTreatmentTypeErrors()?.message}
              />
            )}

            <PriceInput
              register={register('invoiceTotal')}
              errors={getPriceInputErrors()}
              label={dictionary.form.amountPaid}
              shrink={!!watch('invoiceTotal')}
              sx={{ width: isDesktop ? '376px' : '100%' }}
              value={watch('invoiceTotal')}
            />
          </Stack>
          <SelectedInvoiceFile file={invoice.file} watchFile={watchFile} />
          <Buttons
            isEdit={editedInvoiceIndex !== null}
            resetAndExit={() =>
              checkIfShowCancelEditModal()
                ? onReturnFromEdit(onSubmit)
                : resetAndExit()
            }
            onSubmit={onSubmit}
          />
        </Stack>
      </Stack>
    </FormProvider>
  );
};
